import { useCallback, useEffect, useRef, useState } from "react";
import { formatTextAnimationPresetName } from "./utils/keyframeHelpers";
import { TIMELINE_MOTION_PHASES } from "./utils/animationPresetHelpers";
import type { TimelineMotionPhase } from "./utils/animationPresetHelpers";
import {
  TEXT_ANIMATION_PHASE_LABELS,
  TIMELINE_MOTION_PRESET_DIRECTIONS,
} from "./utils/timelineLayerAnimationUtils";
import type { TimelineMotionPresetDirection } from "./utils/timelineLayerAnimationUtils";

const MOTION_PRESET_ASSET_BASE =
  "https://assets.blings.io/platform/motion-presets-thumbs-vids";
const MOTION_PRESET_FALLBACK_DURATION_SECONDS = 3.13;
const PULSE_MOTION_PRESET_PHASES = ["in"] as const;

const TIMELINE_MOTION_PRESET_DIRECTION_LABELS: Record<
  TimelineMotionPresetDirection,
  string
> = {
  left: "Left",
  right: "Right",
  up: "Up",
  down: "Down",
};

export type MotionPresetGalleryItem = {
  key: string;
  presetName: string;
  assetName: string;
  label: string;
  direction?: TimelineMotionPresetDirection;
  phases: readonly TimelineMotionPhase[];
};

export type MotionPresetSelectHandler = (
  presetName: string,
  phase: TimelineMotionPhase,
  direction?: TimelineMotionPresetDirection
) => void;

export function buildMotionPresetGalleryItems(
  presetNames: readonly string[]
): MotionPresetGalleryItem[] {
  const slideItems = TIMELINE_MOTION_PRESET_DIRECTIONS.map((direction) => ({
    key: `slide-${direction}`,
    presetName: "slide",
    assetName: `slide-${direction}`,
    label: TIMELINE_MOTION_PRESET_DIRECTION_LABELS[direction],
    direction,
    phases: TIMELINE_MOTION_PHASES,
  }));
  const otherItems = presetNames
    .filter((presetName) => presetName !== "slide")
    .map((presetName) => ({
      key: presetName,
      presetName,
      assetName: presetName,
      label: formatTextAnimationPresetName(presetName),
      phases:
        presetName === "pulse"
          ? PULSE_MOTION_PRESET_PHASES
          : TIMELINE_MOTION_PHASES,
    }));

  return [...slideItems, ...otherItems];
}

function getMotionPresetThumbUrl(assetName: string): string {
  return `${MOTION_PRESET_ASSET_BASE}/thumb/${assetName}.png`;
}

function getMotionPresetVideoUrl(assetName: string): string {
  return `${MOTION_PRESET_ASSET_BASE}/vid/${assetName}.mp4`;
}

function getMotionPresetVideoBounds(
  video: HTMLVideoElement,
  phase: TimelineMotionPhase
): { start: number; end: number } {
  const duration =
    Number.isFinite(video.duration) && video.duration > 0
      ? video.duration
      : MOTION_PRESET_FALLBACK_DURATION_SECONDS;
  const midpoint = duration / 2;

  if (phase === "in") {
    return { start: 0, end: midpoint };
  }

  if (phase === "out") {
    return { start: midpoint, end: duration };
  }

  return { start: 0, end: duration };
}

function getMotionPresetActionLabel(
  item: MotionPresetGalleryItem,
  phase: TimelineMotionPhase
): string {
  return item.presetName === "pulse"
    ? "Add"
    : TEXT_ANIMATION_PHASE_LABELS[phase];
}

type MotionPresetGalleryProps = {
  items: MotionPresetGalleryItem[];
  onSelect: MotionPresetSelectHandler;
};

export function MotionPresetGallery({
  items,
  onSelect,
}: MotionPresetGalleryProps) {
  if (!items.length) {
    return null;
  }

  return (
    <div
      className="timeline-motion-preset-gallery"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="timeline-motion-preset-gallery__header">
        Motion presets
      </div>
      <div className="timeline-motion-preset-gallery__grid">
        {items.map((item) => (
          <MotionPresetGalleryCard
            key={item.key}
            item={item}
            onSelect={onSelect}
          />
        ))}
      </div>
      <div className="timeline-motion-preset-gallery__hint">
        Add other types of animation, and add more keyframes with the timeline
        plus button, then adjust timing directly on the timeline.
      </div>
    </div>
  );
}

type MotionPresetGalleryCardProps = {
  item: MotionPresetGalleryItem;
  onSelect: MotionPresetSelectHandler;
};

function MotionPresetGalleryCard({
  item,
  onSelect,
}: MotionPresetGalleryCardProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [previewPhase, setPreviewPhase] = useState<TimelineMotionPhase | null>(
    null
  );
  const thumbUrl = getMotionPresetThumbUrl(item.assetName);
  const videoUrl = getMotionPresetVideoUrl(item.assetName);

  const playPreviewSegment = useCallback(() => {
    const video = videoRef.current;
    if (!video || !previewPhase) {
      return;
    }

    const { start, end } = getMotionPresetVideoBounds(video, previewPhase);
    if (video.currentTime < start || video.currentTime >= end) {
      video.currentTime = start;
    }
    video.play().catch(() => undefined);
  }, [previewPhase]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    if (!previewPhase) {
      video.pause();
      video.currentTime = 0;
      return;
    }

    playPreviewSegment();
  }, [playPreviewSegment, previewPhase]);

  return (
    <div className="timeline-motion-preset-gallery__card">
      <div className="timeline-motion-preset-gallery__media">
        <img src={thumbUrl} alt="" draggable={false} />
        <video
          ref={videoRef}
          className={
            previewPhase
              ? "timeline-motion-preset-gallery__video timeline-motion-preset-gallery__video--active"
              : "timeline-motion-preset-gallery__video"
          }
          src={videoUrl}
          poster={thumbUrl}
          muted
          playsInline
          preload="metadata"
          aria-hidden="true"
          onLoadedMetadata={playPreviewSegment}
          onTimeUpdate={() => {
            const video = videoRef.current;
            if (!video || !previewPhase) {
              return;
            }

            const { start, end } = getMotionPresetVideoBounds(
              video,
              previewPhase
            );
            if (video.currentTime >= end || video.currentTime < start) {
              video.currentTime = start;
              video.play().catch(() => undefined);
            }
          }}
        />
      </div>
      <div className="timeline-motion-preset-gallery__label" title={item.label}>
        {item.label}
      </div>
      <div className="timeline-motion-preset-gallery__actions">
        {item.phases.map((phase) => (
          <button
            key={phase}
            type="button"
            className="timeline-motion-preset-gallery__action"
            onMouseEnter={() => setPreviewPhase(phase)}
            onMouseLeave={() => setPreviewPhase(null)}
            onFocus={() => setPreviewPhase(phase)}
            onBlur={() => setPreviewPhase(null)}
            onClick={(e) => {
              e.stopPropagation();
              onSelect(item.presetName, phase, item.direction);
            }}
          >
            {getMotionPresetActionLabel(item, phase)}
          </button>
        ))}
      </div>
    </div>
  );
}
